import { ResFile } from 'src/types';

import { AuthType } from 'src/types/membership';
import { BasicListParams, NotificationParam } from 'src/types/common';

import axiosInstance from './instance';

const memberApi = {
  // 첫 로그인 이후 회원 인증 API (로그인 대체 API)
  // https://dev.clp.kr/anchor/api/static/docs/front/account.html#resources-member-auth
  authCheck() {
    return axiosInstance.get('/membership/member/auth');
  },

  // 패스워드 변경(Change Password)
  // https://dev.clp.kr/spc/api/static/docs/front/account.html#resources-account-change-password
  changePassword(params: { newPassword: string; password: string }) {
    return axiosInstance.put('/membership/member/password', params);
  },

  // 패스워드 다음에 변경(set change after password)
  changePasswordNext(params: object) {
    return axiosInstance.put('/membership/member/password/next', params);
  },

  // 프로필 변경(Change Profile)
  // https://dev.clp.kr/spc/api/static/docs/front/account.html#resources-account-change-profile
  uploadProileImage(params: { file: ResFile }) {
    return axiosInstance.put('/membership/member/profile', params);
  },

  // 프로필 조회(get member profile)
  // https://dev.clp.kr/spc/api/static/docs/front/account.html#resources-account-fetch-profile
  getProileImage() {
    return axiosInstance.get('/membership/member/profile');
  },

  // 리워드 정보 조회
  getMemberReward() {
    return axiosInstance.get('/membership/member/reward');
  },

  // 소셜 회원 조회
  // https://dev.clp.kr/spc/api/static/docs/front/account.html#resources-account-social
  getMemberSocial() {
    return axiosInstance.get('/membership/member/social');
  },

  // 비밀번호 체크 {password: password}
  // https://dev.clp.kr/spc/api/static/docs/front/account.html#resources-account-check-password
  checkPassword(params: { password: string }) {
    return axiosInstance.put('/membership/member/checkPassword', params);
  },

  // 탈퇴요청 params: {reason: Object, comment: String}
  putWithdraw(params: { reason: object; comment: string }) {
    return axiosInstance.put('/membership/member/withdrawal/request', params);
  },

  // 탈퇴사유
  getWithdrawList() {
    return axiosInstance.get('/codes/code/WITHDRAWAL_REASON');
  },

  // 사용자 상세 정보 조회(get member information)
  getMemberInfo() {
    return axiosInstance.get('/membership/member');
  },
  // 이메일 인증 요청 메일 전송(Send email verification requests)
  // https://dev.clp.kr/anchor/api/static/docs/front/account.html#resources-email-auth-request
  emailVarification(params: AuthType) {
    return axiosInstance.post('/member/membership/auth/email/request', params);
  },

  emailCodeVerfy(params: object) {
    return axiosInstance.post('/member/membership/auth/email', params);
  },
  // User sign-up authentication request by responsibility
  // https://dev.clp.kr/spc/api/static/docs/guest/guest_account.html#resources-guest-register-auth-brand-request-responsibility
  authResponsibility(params: { code: string }) {
    return axiosInstance.post('/member/membership/auth/responsibility', params);
  },
  // 약관 조회(get Terms and Conditions)
  // https://dev.clp.kr/anchor/api/static/docs/guest/guest_account.html#resources-guest-brand-terms
  termsAgree(params: { terms: { TERMS_OF_SERVICE: number; PRIVACY_STATEMENT: number } }) {
    return axiosInstance.put('/membership/member/termsAgree', params);
  },

  // 사용자 포인트 랭킹(Point ranking) 조회
  // https://dev.clp.kr/spc/api/static/docs/front/account.html#resources-leader-board
  leaderBoard() {
    return axiosInstance.get('/membership/point/ranking');
  },

  // 사용자 포인트 랭킹(Point ranking) 조회
  // https://dev.clp.kr/spc/api/static/docs/front/account.html#resources-leader-board
  leaderBoardStores(params: { page: number; size: number }) {
    return axiosInstance.get('/membership/point/ranking/store', { params });
  },

  // 사용자 포인트 랭킹(Point ranking) 조회
  // https://dev.clp.kr/spc/api/static/docs/front/account.html#resources-leader-board
  leaderBoardMyStore() {
    return axiosInstance.get('/membership/point/ranking/store/my');
  },

  myStoreRanking() {
    return axiosInstance.get('/membership/point/ranking/store');
  },

  addPhoneNo(params: {
    phone: {
      country: string;
      phone: string;
    };
  }) {
    return axiosInstance.put('/membership/member', params);
  },

  addDateOfBirth(params: object) {
    return axiosInstance.put('/membership/member', params);
  },

  updateEditInfo(params: {
    phone: {
      country: string;
      phone: string;
    };
    birth: string;
  }) {
    return axiosInstance.put('/membership/member', params);
  },

  memberPoint() {
    return axiosInstance.get('/membership/member/point');
  },

  languageChangePushNotification(params: { option: { pushToken: string; pushLanguage: string } }) {
    return axiosInstance.put('/membership/member/options', params);
  },

  withdrawAccountDirect(id: string, params: { reason: object; comment: string }) {
    return axiosInstance.put(`/membership/member/${id}/withdrawal`, params);
  },

  withdrawAccountRequest(params: { reason: object; comment: string }) {
    return axiosInstance.put(`/membership/member/withdrawal/request`, params);
  },

  // 소셜로그인
  integrateSocialAccount(params: object) {
    return axiosInstance.post('/social/integration', params);
  },

  // 차단 사용자 목록
  getBlockList(params: BasicListParams) {
    return axiosInstance.get(`/member/block`, { params });
  },

  // 사용자 차단
  requestUserBlock(targetId: string) {
    return axiosInstance.post(`/member/block/${targetId}`);
  },

  // 사용자 차단 해제
  restoreBlock(targetId: string) {
    return axiosInstance.delete(`/member/block/${targetId}`);
  },

  // 신고 사유 리스트
  getReportReasonList() {
    return axiosInstance.get('/codes/code/REPORT_REASON/combo');
  },

  // 학습자 메뉴접근 권한 조회
  getCheckInfo() {
    return axiosInstance.get('/auth/info');
  },

  // 사용자 상세 정보 조회(Account Info Detail Inquiry)
  // https://dev.clp.kr/anchor/api/static/docs/front/account.html#resources-account-fetch-info
  fetchMember() {
    return axiosInstance.get(`/membership/member`);
    // return axiosInstance.get(`/board/SNS1/article/list`,params)
  },

  callPushNotificationApi(params: NotificationParam) {
    return axiosInstance.put(`/membership/member/options`, params);
  },
};

export default memberApi;
